import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";

const EMPTY = {
    title: "",
    location: "",
    start_date: "",
    end_date: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    description: "",
};

function deriveMonthYear(start) {
    if (!start) return null;
    const d = new Date(start);
    if (isNaN(d.getTime())) return null;
    return { month: d.getMonth() + 1, year: d.getFullYear() };
}

export default function EventsManager() {
    const [events, setEvents] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/events");
            setEvents(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const reset = () => {
        setEditing(null);
        setForm(EMPTY);
    };

    const save = async (e) => {
        e.preventDefault();
        const my = deriveMonthYear(form.start_date);
        const payload = {
            ...form,
            month: my ? my.month : Number(form.month),
            year: my ? my.year : Number(form.year),
            end_date: form.end_date || form.start_date,
        };
        try {
            if (editing) {
                await api.put(`/admin/events/${editing}`, payload);
                toast.success("Évènement mis à jour");
            } else {
                await api.post("/admin/events", payload);
                toast.success("Évènement créé");
            }
            reset();
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cet évènement ?")) return;
        try {
            await api.delete(`/admin/events/${id}`);
            toast.success("Supprimé");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const startEdit = (ev) => {
        setEditing(ev.id);
        setForm({
            title: ev.title,
            location: ev.location,
            start_date: ev.start_date,
            end_date: ev.end_date,
            month: ev.month,
            year: ev.year,
            description: ev.description || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div data-testid="admin-events-page">
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                Évènements
            </h1>
            <div className="mt-3 brand-card p-4 bg-[#FFF1E0]">
                <p className="text-sm text-[#3D1F0F] leading-relaxed">
                    <strong>Comment ça marche :</strong> chaque évènement a une date de
                    début et une date de fin. Il apparaît automatiquement sur la home
                    pendant tout le ou les mois qu'il couvre — vous n'avez rien à
                    activer ni désactiver le moment venu.
                </p>
                <ul className="mt-2 text-xs text-[#5C3D2E] space-y-1 list-disc pl-5">
                    <li>Event du 5 au 10 mai → visible en mai.</li>
                    <li>Event du 28 avril au 5 mai → visible en avril, puis en mai.</li>
                    <li>Vous pouvez planifier vos évènements des mois à l'avance.</li>
                </ul>
            </div>

            <section className="brand-card p-6 mt-6">
                <h2 className="font-serif-display text-2xl text-[#2A1408] mb-4">
                    {editing ? "Modifier l’évènement" : "Nouvel évènement"}
                </h2>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Titre
                        <input
                            required
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="event-title"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Lieu
                        <input
                            required
                            value={form.location}
                            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="event-location"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Date de début
                        <input
                            required
                            type="date"
                            value={form.start_date}
                            onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="event-start"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Date de fin
                        <input
                            type="date"
                            value={form.end_date}
                            onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="event-end"
                        />
                    </label>
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Description
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="event-desc"
                        />
                    </label>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="brand-btn" data-testid="event-submit">
                            {editing ? "Mettre à jour" : "Ajouter"}
                        </button>
                        {editing && (
                            <button
                                type="button"
                                onClick={reset}
                                className="brand-btn-outline"
                            >
                                Annuler
                            </button>
                        )}
                    </div>
                </form>
            </section>

            <section className="mt-8">
                <h2 className="font-serif-display text-2xl text-[#2A1408] mb-4">
                    Liste des évènements
                </h2>
                <div className="space-y-3">
                    {events.length === 0 && (
                        <p className="text-[#5C3D2E]">Aucun évènement.</p>
                    )}
                    {events.map((ev) => (
                        <article
                            key={ev.id}
                            className="brand-card p-5 flex justify-between items-start gap-3"
                            data-testid={`admin-event-${ev.id}`}
                        >
                            <div>
                                <p className="text-xs uppercase tracking-widest text-[#B07B66]">
                                    {ev.start_date} → {ev.end_date} · {ev.month}/{ev.year}
                                </p>
                                <p className="font-serif-display text-lg text-[#2A1408]">
                                    {ev.title}
                                </p>
                                <p className="text-sm text-[#5C3D2E]">{ev.location}</p>
                                {ev.description && (
                                    <p className="text-sm text-[#5C3D2E] mt-1">{ev.description}</p>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => startEdit(ev)}
                                    className="text-[#6B4226]"
                                    aria-label="Modifier"
                                >
                                    <Pencil size={16} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(ev.id)}
                                    className="text-red-700"
                                    aria-label="Supprimer"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
}
